import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../environments/environment';

declare const google: {
    accounts: {
        id: {
            initialize: (config: object) => void;
            renderButton: (el: HTMLElement, options: object) => void;
        };
    };
};

/**
 * Loads Google Identity Services once. GIS allows one global callback; we delegate via setCredentialHandler.
 */
@Injectable({ providedIn: 'root' })
export class GoogleIdentityService {
    private readonly ready$ = new ReplaySubject<void>(1);
    private initialized = false;
    private credentialHandler: (token: string) => void = () => {};

    /** Route ID tokens to the active screen (hero vs modal). */
    setCredentialHandler(handler: (token: string) => void): void {
        this.credentialHandler = handler;
    }

    init(): void {
        const clientId = environment.googleClientId?.trim();
        if (environment.googleSignInEnabled === false) {
            return;
        }
        if (!clientId || this.initialized) {
            if (!clientId) {
                console.warn('GoogleIdentityService: environment.googleClientId is not set.');
            }
            return;
        }

        const runInit = () => {
            google.accounts.id.initialize({
                client_id: clientId,
                callback: (resp: { credential: string }) => this.credentialHandler(resp.credential),
                auto_select: false,
                cancel_on_tap_outside: true
            });
            this.initialized = true;
            this.ready$.next();
        };

        if (typeof google !== 'undefined' && google.accounts?.id) {
            runInit();
            return;
        }

        const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
        if (existing) {
            existing.addEventListener('load', runInit);
            return;
        }

        const s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true;
        s.defer = true;
        s.onload = () => runInit();
        document.head.appendChild(s);
    }

    renderButton(element: HTMLElement, width?: number): void {
        if (environment.googleSignInEnabled === false) {
            return;
        }
        this.ready$.pipe(take(1)).subscribe(() => {
            if (typeof google === 'undefined' || !google.accounts?.id) {
                return;
            }
            element.innerHTML = '';
            const w = width ?? Math.max(280, element.clientWidth || 320);
            google.accounts.id.renderButton(element, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: 'continue_with',
                width: w,
                shape: 'rectangular'
            });
        });
    }
}
